import { useMemo, useState } from 'react';
import { Box, makeStyles, ButtonBase } from '@material-ui/core';
import moment from 'moment';
import _ from 'lodash';
import Text from '../../../../../components/Text';
import { ReactComponent as DiamondIcon } from '../../../../../static/images/DiamondIcon.svg';
import { ReactComponent as RunIcon } from '../../../../../static/images/RunIcon.svg';
import { DropDownIcon, DropUpIcon } from '../../../../../components/Icon';
import admissionUtil from '../../../../../utils/admissionUtil';

const useStyles = makeStyles((theme) => ({
  column: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: '6.75rem',
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '0.75rem',
    color: '#7d86a9',
    textTransform: 'uppercase',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '2.5rem',
    width: '100%',
  },
  finishBus: {
    borderRadius: '20px',
    border: '2px solid #EFAA7E',
    backgroundColor: '#FFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '1.5rem',
    zIndex: '2',
    position: 'relative',
  },
  pendingBus: {
    borderRadius: '50%',
    width: '1.5rem',
    height: '1.5rem',
    border: '2px solid #FECA06',
    backgroundColor: '#FFF2C1',
    zIndex: '2',
  },
  step: {
    position: 'absolute',
    left: '-100%',
    right: '50%',
    top: '50%',
    height: '2px',
    backgroundColor: '#FECA06',
    zIndex: '1',
  },
  time: {
    height: '1.77rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'underline',
    textDecorationColor: '#7D86A9',
  },
  flag: {
    position: 'absolute',
    right: '-25%',
    top: '50%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.3rem',
    transform: 'translate(50%, calc(-0.7rem + 1px))',
    zIndex: '2',
  },
  diamondIcon: {
    width: '1.4rem',
    height: '1.4rem',
  },
  runIcon: {
    width: '1.625rem',
    height: '1.18rem',
  },
  remarkCard: {
    borderRadius: '4px',
    border: 'solid 1px #e4e7eb',
    padding: '0.313rem 0.313rem 0.313rem 0.563rem',
    backgroundColor: '#FFF',
    position: 'relative',
    '&::before': {
      position: 'absolute',
      top: '0',
      left: '0',
      content: '""',
      display: 'block',
      width: '0.25rem',
      height: '100%',
      backgroundColor: '#e4e7eb',
      zIndex: '9',
    },
  },
  remarkText: {
    color: '#2E4469',
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.75rem',
  },
  viewMoreBtn: {
    width: '9.875rem',
    height: '2rem',
    borderBottom: '1px solid #E4E7EB',
    borderLeft: '1px solid #E4E7EB',
    borderRight: '1px solid #E4E7EB',
    borderRadius: '0 0 4px 4px',
    color: '#7D86A9',
    fontSize: '1rem',
    fontFamily: 'NotoSans-Medium',
  },
  dropIcon: {
    fontSize: '0.96rem',
    color: '#7D86A9',
    marginLeft: '0.63rem',
  },
}));

const BusStatus = ({ busTime, first, flag }) => {
  const classes = useStyles();

  return (
    <Box position="relative">
      {busTime !== 'Invalid date' ? (
        <Box className={classes.finishBus}>
          <Text color="#333" fontSize="0.75rem">
            {busTime}
          </Text>
        </Box>
      ) : (
        <Box display="flex" alignItems="flex-start" justifyContent="center">
          <div className={classes.pendingBus} />
        </Box>
      )}
      {!first && <div className={classes.step} />}
      {flag && (
        <Box className={classes.flag}>
          <DiamondIcon className={classes.diamondIcon} />
          <RunIcon className={classes.runIcon} />
        </Box>
      )}
    </Box>
  );
};

const Card = ({ data }) => {
  const classes = useStyles();

  if (!data.hospCd) {
    return <Box />;
  }

  return (
    <Box display="flex" flexDirection="column" gridGap="0.25rem">
      <Box borderRadius="12px" boxShadow="0px 0px 4px 0px rgba(0, 0, 0, 0.25)" p="0.5rem" bgcolor="#FFF">
        <Text color="#333" fontSize="0.625rem" textAlign="center">
          {`${data.hospCd} ${data.wardLoc}`}
        </Text>
        <Text color="#000" fontSize="0.75rem" textAlign="center">
          {`CORP/${data.actionBy}`}
        </Text>
        {data.actionType === 'C' && (
          <Text color="#D81E06" fontSize="0.5rem" textAlign="center">
            Cancel calling VH C21
          </Text>
        )}
        <Box borderRadius="8px" border="2px solid #EFAA7E" p="0.38rem" bgcolor="#FBEADB" mt="0.37rem">
          <Text color="#000" fontSize="0.625rem" textAlign="center">
            {data.actionType === 'R' || data.actionType === 'C' ? 'Redirect to' : 'Assign to'}
          </Text>
          <Text color="#000" fontSize="0.75rem" textAlign="center">
            {data.assignTo}
          </Text>
        </Box>
      </Box>

      {data.remark && (
        <Box className={classes.remarkCard}>
          <div className={classes.remarkText}>{data.remark}</div>
        </Box>
      )}
    </Box>
  );
};

const CallingCalledUI = ({ bgcolor = '#FFF', p = '0', m = '0 0 1.25rem', editView = false, data, busStopStep }) => {
  const classes = useStyles();
  const [viewMore, setViewMore] = useState(editView ? false : true);

  const busStopData = useMemo(() => {
    let tempData = [
      ..._.map(data, (item) => ({
        ...item,
        busTime: moment(item.actionAtm).format(admissionUtil.busStopDateFormat),
        currentTime: item.actionAtm,
      })),
      {
        busTime: 'Invalid date',
        txnWardAssignId: 0,
      },
    ];

    tempData[tempData.length - 2].flag = true;

    // buffer time
    tempData = _.map(tempData, (item, index) => ({
      ...item,
      time: admissionUtil.mathBuffTime(item.currentTime, tempData[index - 1]?.currentTime),
    }));
    return tempData;
  }, [data, busStopStep]);

  const handleViewMore = () => {
    setViewMore((preData) => !preData);
  };

  return (
    <Box m={m}>
      <Box
        display="flex"
        borderRadius="8px"
        bgcolor={bgcolor}
        p={p}
        gridGap="0.81rem"
        border={editView ? '1px solid #E4E7EB' : 'none'}
      >
        {busStopData.map((item, index) => (
          <Box className={classes.column} key={item.txnWardAssignId}>
            <BusStatus busTime={item.busTime} first={index === 0} flag={item.flag} />
            <div className={classes.time}>
              <Text fontSize="0.75rem">{item.time}</Text>
            </div>
            {viewMore && <Card data={item} />}
          </Box>
        ))}
      </Box>
      {editView && (
        <Box display="flex" justifyContent="flex-end">
          <ButtonBase className={classes.viewMoreBtn} onClick={handleViewMore}>
            View More
            {viewMore ? <DropUpIcon className={classes.dropIcon} /> : <DropDownIcon className={classes.dropIcon} />}
          </ButtonBase>
        </Box>
      )}
    </Box>
  );
};

export default CallingCalledUI;
